import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import type { DatasetMetric } from "@models/Metric";
import type {
  ScorecardBrand,
  ScorecardDatasetMetricRow,
  ScorecardQuestionRow,
} from "@/models/Scorecard";
import RowSourceToggle, { type RowSource } from "./RowSourceToggle";
import QuestionRowBuilder from "./QuestionRowEditor/QuestionRowBuilder";
import DatasetMetricRowBuilder from "./DatasetMetricRowEditor/DatasetMetricRowBuilder";

interface RowBuilderProps {
  datasetId: string | null;
  brands: ScorecardBrand[];
  otherQuestionRows: ScorecardQuestionRow[];
  datasetMetrics: DatasetMetric[];
  editingQuestionRow?: ScorecardQuestionRow | null;
  editingDatasetMetricRow?: ScorecardDatasetMetricRow | null;
  onCancel: () => void;
  onSaveQuestionRow: (row: ScorecardQuestionRow) => void;
  onSaveDatasetMetricRow: (row: ScorecardDatasetMetricRow) => void;
}

/**
 * Shared "Add Row" / "Edit Row" shell: the Row Source toggle (Question/
 * Metric, defaulting to Question) plus whichever sub-builder is active.
 * Replaces the old dedicated "Add Metric Row" builder now that a row can
 * come from either a source question or a saved Dataset Metric. Switching
 * source is only available while adding a brand-new row — an existing row
 * (either kind) is edited in place, no source-switch confirmation flow yet.
 */
const RowBuilder: React.FC<RowBuilderProps> = ({
  datasetId,
  brands,
  otherQuestionRows,
  datasetMetrics,
  editingQuestionRow,
  editingDatasetMetricRow,
  onCancel,
  onSaveQuestionRow,
  onSaveDatasetMetricRow,
}) => {
  const editingRow = editingQuestionRow ?? editingDatasetMetricRow ?? null;
  const [rowSource, setRowSource] = useState<RowSource>(
    editingDatasetMetricRow ? "metric" : "question",
  );

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "primary.main",
        borderRadius: 1,
        p: 1.5,
        mt: 1,
        backgroundColor: "#fff",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        {editingRow ? "Edit Row" : "Add Row"}
      </Typography>

      <RowSourceToggle value={rowSource} onChange={setRowSource} isDisabled={!!editingRow} />

      {rowSource === "question" ? (
        <QuestionRowBuilder
          datasetId={datasetId}
          brands={brands}
          otherRows={otherQuestionRows}
          editingRow={editingQuestionRow}
          onCancel={onCancel}
          onSave={onSaveQuestionRow}
        />
      ) : (
        <DatasetMetricRowBuilder
          datasetId={datasetId}
          metrics={datasetMetrics}
          brands={brands}
          editingRow={editingDatasetMetricRow}
          onCancel={onCancel}
          onSave={onSaveDatasetMetricRow}
        />
      )}
    </Box>
  );
};

export default RowBuilder;
